export const useCasesData = [
  {
    slug: "document-automation",
    title: "Document Automation & Workflows",
    shortDescription: "Streamline your document processes from creation to archiving.",
    longDescription: "Automate repetitive document tasks like generation, data extraction, conversion, and routing. Build efficient workflows that reduce manual effort and errors.",
    icon: "FileCog",
    tags: ["workflow", "automation", "efficiency", "ocr"],
    contentComponent: "DocumentAutomationPage",
    details: {
      overview: "WebFiny empowers businesses to build sophisticated document automation solutions. Our APIs handle everything from template-based document generation to complex workflow orchestration involving OCR, data extraction, conversion, and secure archiving. Reduce manual intervention, accelerate processing times, and ensure consistency across all your document-centric operations.",
      keyFeatures: [
        "Automated PDF generation from templates (HTML, Word)",
        "Data extraction from PDFs (text, tables, forms) with OCR capabilities",
        "Batch processing for high-volume tasks (merge, split, compress)",
        "Secure document watermarking, encryption, and redaction",
        "Webhook integration for real-time workflow updates"
      ],
      benefits: [
        "Drastically reduce manual effort and associated costs.",
        "Minimize errors and improve data accuracy.",
        "Accelerate business processes and improve turnaround times.",
        "Enhance compliance with standardized document handling.",
        "Scale operations efficiently without proportional resource increase."
      ],
      exampleUseCases: [
        "Automated invoice processing and data entry.",
        "Contract generation and lifecycle management.",
        "Client onboarding document assembly and verification.",
        "Batch report generation and distribution.",
        "Automated archiving and records management."
      ],
      relatedSolutions: ["api-integration", "publishing", "legal-healthcare"],
      image: "https://images.unsplash.com/photo-1589244825599-7541788637fade?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8YXV0b21hdGlvbnxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=600&q=80"
    }
  },
  {
    slug: "api-integration",
    title: "API Integration & Developer Tools",
    shortDescription: "Seamlessly integrate PDF functionalities into your existing applications.",
    longDescription: "Our robust REST APIs and comprehensive SDKs make it easy for developers to add powerful PDF processing capabilities to any software or platform.",
    icon: "Puzzle", 
    tags: ["api", "sdk", "developer", "integration"],
    contentComponent: "ApiIntegrationPage",
    details: {
      overview: "WebFiny's APIs are designed for developers first. We provide clear documentation, robust SDKs in popular languages, and a developer-friendly platform to help you integrate advanced PDF functionalities into your applications quickly and efficiently. Whether you're building a web app, mobile app, or backend service, our APIs offer the flexibility and power you need.",
      keyFeatures: [
        "Well-documented RESTful APIs with predictable responses.",
        "SDKs for popular programming languages (e.g., JavaScript, Python, Java, C#).",
        "Secure authentication and authorization mechanisms (API keys, OAuth).",
        "Detailed API logs and debugging tools in the developer dashboard.",
        "Scalable infrastructure to handle high request volumes."
      ],
      benefits: [
        "Reduce development time and effort for PDF features.",
        "Ensure seamless integration with your existing tech stack.",
        "Access a wide range of PDF functionalities through a unified API.",
        "Benefit from a reliable and scalable platform maintained by experts.",
        "Stay focused on your core application logic while we handle PDF complexities."
      ],
      exampleUseCases: [
        "Adding 'Export to PDF' functionality to web applications.",
        "Integrating PDF signing workflows into CRM systems.",
        "Building custom document management solutions.",
        "Automating PDF generation within ERP systems.",
        "Powering mobile apps with PDF viewing and manipulation features."
      ],
      relatedSolutions: ["document-automation", "ecommerce", "publishing"],
      image: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fGFwaSUyMGludGVncmF0aW9ufGVufDB8fDB8fHww&auto=format&fit=crop&w=600&q=80"
    }
  },
  {
    slug: "publishing",
    title: "Publishing & Print Production",
    shortDescription: "Generate press-ready PDFs for books, magazines, and marketing materials.",
    longDescription: "Control every aspect of your print output, from page layout and color profiles to font embedding and pre-flight checks, ensuring professional quality.",
    icon: "BookOpen",
    tags: ["print", "publishing", "cmyk", "prepress"],
    contentComponent: "PublishingPage",
    details: {
      overview: "WebFiny provides precision tools for the publishing industry. Generate print-ready PDFs for books, magazines, brochures, and other materials. Control aspects like page dimensions, margins, color profiles (CMYK), and metadata. Flatten documents to ensure consistent print output and embed fonts for professional results. Our API simplifies the creation of high-quality, press-ready files, automating pre-press tasks and reducing manual errors. Integrate WebFiny into your content management systems or editorial workflows to automatically generate proofs, final print files, or digital editions with precise control over layout and print specifications.",
      keyFeatures: [
        "CMYK color profile support for accurate print reproduction.",
        "Font embedding and subsetting for consistent typography.",
        "Control over PDF/X standards for print compliance.",
        "Automated imposition and pre-flight checks (planned).",
        "High-resolution image handling and optimization for print."
      ],
      benefits: [
        "Ensure consistent and professional print quality.",
        "Automate time-consuming pre-press tasks.",
        "Reduce errors and costly reprints.",
        "Streamline editorial and production workflows.",
        "Easily generate multiple versions for different print or digital outputs."
      ],
      exampleUseCases: [
        "Automated generation of book layouts from manuscript files.",
        "Creating print-ready marketing brochures with variable data.",
        "Producing high-quality magazine spreads with precise color management.",
        "Generating digital proofs for client review.",
        "Converting web content to print-optimized PDFs."
      ],
      relatedSolutions: ["document-automation", "ecommerce", "api-integration"],
      image: "https://images.unsplash.com/photo-1456234195398-2062d168003a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8cHJpbnQlMjBwcm9kdWN0aW9ufGVufDB8fDB8fHww&auto=format&fit=crop&w=600&q=80"
    }
  },
  {
    slug: "ecommerce",
    title: "E-commerce Document Solutions",
    shortDescription: "Automate invoices, packing slips, and product catalogs for online stores.",
    longDescription: "Enhance your e-commerce operations by automatically generating professional PDFs for customer communication, order fulfillment, and marketing.",
    icon: "ShoppingCart",
    tags: ["ecommerce", "invoice", "catalog", "automation"],
    contentComponent: "EcommercePage",
    details: {
      overview: "WebFiny helps e-commerce businesses streamline their operations by automating the generation of crucial documents. Create professional invoices, packing slips, shipping labels, and return forms directly from your order management system. Generate dynamic product catalogs or specification sheets. Our APIs integrate seamlessly with popular e-commerce platforms and custom-built systems, ensuring your document workflows are as efficient as your online sales.",
      keyFeatures: [
        "Automated invoice and packing slip generation with order data.",
        "Dynamic product catalog creation with up-to-date information.",
        "Barcode generation for shipping labels and inventory management.",
        "Personalized marketing inserts or thank-you notes in PDF.",
        "Secure delivery of digital product guides or warranty documents."
      ],
      benefits: [
        "Save time and reduce manual effort in order fulfillment.",
        "Improve professionalism with consistent, branded documents.",
        "Reduce errors in shipping and invoicing.",
        "Enhance customer experience with timely and accurate documentation.",
        "Easily update product information across catalogs and spec sheets."
      ],
      exampleUseCases: [
        "Generating PDF invoices automatically when an order is placed.",
        "Creating packing slips with order details and shipping information.",
        "Producing downloadable PDF product manuals for customers.",
        "Compiling personalized product recommendation flyers.",
        "Batch generating return labels for customer convenience."
      ],
      relatedSolutions: ["document-automation", "api-integration", "publishing"],
      image: "https://images.unsplash.com/photo-1580974928084-ba2786195794?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8ZWNvbW1lcmNlJTIwcGFja2FnaW5nfGVufDB8fDB8fHww&auto=format&fit=crop&w=600&q=80"
    }
  },
  {
    slug: "legal-healthcare",
    title: "Legal & Healthcare Document Management",
    shortDescription: "Securely manage sensitive documents, redact information, and ensure compliance.",
    longDescription: "WebFiny provides tools for industries with strict compliance needs, offering secure PDF creation, redaction, watermarking, and archival solutions.",
    icon: "Gavel", 
    tags: ["legal", "healthcare", "compliance", "security", "redaction"],
    contentComponent: "LegalHealthcarePage",
    details: {
      overview: "For industries like legal and healthcare, document security and compliance are paramount. WebFiny offers robust features to manage sensitive PDF documents, including secure redaction of confidential information, password protection, encryption, and auditable watermarking. Our APIs help you build workflows that adhere to regulatory requirements like HIPAA or GDPR, ensuring patient data or client information is handled with the utmost care.",
      keyFeatures: [
        "Secure PDF redaction to permanently remove sensitive content.",
        "AES 256-bit encryption and password protection.",
        "Customizable watermarking for document tracking and status indication.",
        "PDF/A conversion for long-term archival and compliance.",
        "Detailed logging and audit trails for processing activities (feature enhancement)."
      ],
      benefits: [
        "Protect sensitive information and maintain confidentiality.",
        "Meet industry-specific compliance requirements (e.g., HIPAA, GDPR).",
        "Securely share documents with authorized parties.",
        "Ensure long-term readability and integrity of archived documents.",
        "Reduce risk associated with handling confidential data."
      ],
      exampleUseCases: [
        "Redacting patient information from medical records before sharing.",
        "Securing legal contracts with passwords and encryption.",
        "Watermarking court documents with case numbers and confidentiality notices.",
        "Converting legal briefs to PDF/A for archival.",
        "Automating the generation of compliant patient consent forms."
      ],
      relatedSolutions: ["document-automation", "security-features", "api-integration"],
      image: "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8aGVhbHRoY2FyZSUyMGRvY3VtZW50c3xlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=600&q=80"
    }
  },
  {
    slug: "real-estate",
    title: "Real Estate Document Processing",
    shortDescription: "Automate property reports, contracts, and marketing materials.",
    longDescription: "Streamline real estate paperwork by generating property listings, sales agreements, and appraisal reports dynamically from your data sources.",
    icon: "Home",
    tags: ["real estate", "contracts", "reports", "listings"],
    contentComponent: "RealEstatePage",
    details: {
      overview: "The real estate industry relies heavily on accurate and timely documentation. WebFiny helps automate the creation and management of property reports, sales contracts, lease agreements, and marketing brochures. Integrate our APIs with your CRM or property management system to dynamically generate personalized documents, reducing manual data entry and ensuring all paperwork is consistent and professional.",
      keyFeatures: [
        "Dynamic generation of property listing sheets with images and details.",
        "Automated creation of sales contracts and lease agreements from templates.",
        "Merging multiple documents (e.g., disclosures, addendums) into a single package.",
        "Watermarking documents as 'Draft' or 'Confidential'.",
        "Converting property photos and floor plans into PDF brochures."
      ],
      benefits: [
        "Speed up the creation of property-related documents.",
        "Ensure accuracy and consistency across all paperwork.",
        "Improve the professionalism of your marketing materials.",
        "Reduce time spent on administrative tasks.",
        "Easily manage and archive large volumes of property documents."
      ],
      exampleUseCases: [
        "Generating PDF property brochures from MLS data.",
        "Automating the creation of personalized offer letters.",
        "Compiling closing document packages for real estate transactions.",
        "Creating inspection reports with embedded images.",
        "Converting scanned lease agreements to searchable PDFs using OCR."
      ],
      relatedSolutions: ["document-automation", "api-integration", "ecommerce"],
      image: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8cmVhbCUyMGVzdGF0ZSUyMGRvY3VtZW50c3xlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=600&q=80"
    }
  },
];


export const getUseCaseBySlug = (slug) => {
  return useCasesData.find(useCase => useCase.slug === slug);
};

export const getUseCaseContentComponent = (slug) => {
  const useCase = getUseCaseBySlug(slug);
  if (!useCase || !useCase.contentComponent) return null;

  switch (useCase.contentComponent) {
    case "DocumentAutomationPage":
      return "DocumentAutomationPage";
    case "ApiIntegrationPage":
      return "ApiIntegrationPage";
    case "PublishingPage":
      return "PublishingPage";
    case "EcommercePage":
      return "EcommercePage";
    case "LegalHealthcarePage":
      return "LegalHealthcarePage";
    case "RealEstatePage":
      return "RealEstatePage";
    default:
      return null;
  }
};